/**
 * Responsible for all communication with the server.
 *
 * Will display a user provided login window to allow.
 */

import $ from "jquery";
import * as _ from "underscore";

import * as chromeExtensionRequestClient from "chromeExtensionRequestClient";
import * as googleAuth from "googleAuth";
import * as localAPI from "localAPI";
import * as notificationBar from "notificationBar";
import * as offlineNotice from "offlineNotice";
import * as readlangLocalStorage from "readlangLocalStorage";
import * as sharedConfig from "shared/sharedConfig";

/**
 * Delay before each retry of a request that failed with status 0 (no response
 * at all). The first retry is immediate: the usual cause is a keep-alive
 * socket the server closed while the page sat idle, which is cured by simply
 * opening a new connection — blindly waiting there turned a sub-second word
 * lookup into a multi-second stall. Later attempts back off for real network
 * loss.
 */
const retryDelaysMs = [0, 5000, 15000];

export type ReadlangResponse<T> = {
  data: T;
  textStatus: string | number | undefined;
  jsXHR: JQuery.jqXHR | undefined;
};

class ReadlangClient {
  private user: any;
  private checkingForLogout: boolean;
  private baseURL: string;
  private globalOptions: {
    [key: string]: any;
    unauthorizedHandler?: () => void;
  } = {};
  private checkTimeout?: ReturnType<typeof setTimeout>;
  private retryTimeoutIDs: Set<ReturnType<typeof setTimeout>> = new Set();
  private promptForCredentials: (options: any) => void = () => {};

  constructor(baseURL: string) {
    this.user = null;
    this.checkingForLogout = false;
    this.baseURL = baseURL;
    // This conditional is a hacky fix - this module shouldn't be loaded at all in the embedded reader
    if (/localhost|readlang/.test(window.location.host)) {
      $(window).focus(() => {
        clearTimeout(this.checkTimeout);
        this.checkTimeout = setTimeout(() => {
          this.checkForLogoutInOtherTab();
        }, 30);
      });
    }
  }

  public setSignupModal(signupModal: any) {
    this.promptForCredentials = signupModal.show;
  }

  /**
   * Whether the caller can stream a response with a raw fetch. Inside the
   * browser extension's iframe a direct fetch carries no session cookie when
   * third-party cookies are blocked, so requests must go through request()
   * (routed via the extension's background script), which can't stream.
   */
  public canStreamResponses() {
    return !!window.fetch && !window.READLANG?.chromeExtension;
  }

  public setOption(key: string, value: any) {
    this.globalOptions[key] = value;
  }

  private checkForLogoutInOtherTab() {
    if (this.checkingForLogout) {
      return;
    }
    this.checkingForLogout = true;

    readlangLocalStorage.getItem("loggedInEmail", (loggedInEmail) => {
      this.checkingForLogout = false;
      if (this.user?.isLocalMode?.value) {
        if (loggedInEmail) {
          readlangLocalStorage.removeItem("loggedInEmail");
        }
        return;
      }
      if (
        this.user &&
        !this.user.isNew.value &&
        (this.user.state.value.email || loggedInEmail) &&
        this.user.state.value.email !== loggedInEmail
      ) {
        // TODO: replace with nicer alert
        //alert('You have logged out in another tab, this tab will now refresh');
        window.location.reload();
      }
    });
  }

  async handleError(status, data, errorCallback) {
    const that = this;

    if (status === 401) {
      if (that.globalOptions.unauthorizedHandler) {
        that.globalOptions.unauthorizedHandler();
      } else {
        await readlangLocalStorage.setItem("localMode", true);
        window.location.reload();
        return;
      }
    } else if (status === 0 && !navigator.onLine) {
      offlineNotice.show();
    } else if (status === 0) {
      notificationBar.showMessage({
        htmlMessage:
          'Connection to server lost. Please check you are connected to the internet, then <a href="' +
          window.location.href +
          '" data-bypass>reload this page</a>.',
        above: true,
        time: 3600 * 1000,
        element: $("body"),
      });
    } else if (status >= 500) {
      notificationBar.showMessage({
        htmlMessage:
          "Error communicating with server (" +
          status +
          ").<br><br>" +
          'Try <a data-bypass href="' +
          window.location.href +
          '">reloading the page</a>.',
        above: true,
        time: 3600 * 1000,
        element: $("body"),
      });
      console.error(
        "Server error response: %s (%s)\ntext status: %s\nresponse text: %s",
        // errorThrown,
        status,
        data
        // responseText
      );
    } else {
      if (errorCallback) {
        errorCallback({ status, data });
      } else {
        // show notification to user - advise page refresh
        console.error(
          "Remote request error: %s (%s)\ntext status: %s\nresponse text: %s",
          // errorThrown,
          status
          // jqXHR.responseText
        );

        notificationBar.showMessage({
          htmlMessage:
            "Unexpected error communicating with server (" +
            status +
            '). <a href="' +
            window.location.href +
            '" data-bypass>Reload page</a>',
          above: true,
          time: 3600 * 1000,
          element: $("body"),
        });
      }
      return;
    }

    // The branches above (401 with an unauthorizedHandler, network loss, 5xx)
    // show a global notification, but the request is still over: without this
    // call the caller's error callback never runs (and, via request()'s
    // wrapper, its promise never settles), leaving any in-flight UI state —
    // e.g. a disabled upload button — stuck forever. `alreadyNotified` lets
    // callers skip showing a second error message of their own.
    errorCallback?.({ status, data, alreadyNotified: true });
  }

  public async remoteRequest(
    method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
    path: string,
    data: any,
    callback: any,
    errorCallback: any,
    jQueryOptions?: any,
    retryAttempt = 0
  ) {
    const that = this;
    this.checkForLogoutInOtherTab();

    const ajaxOptions = _.extend(
      {
        type: method,
        url: this.baseURL + path,
        data: data,
        dataType: "json",
        xhrFields: {
          withCredentials: true,
        },
        success: (responseData, textStatus, jsXHR) => {
          // Check for API compatibility version mismatch
          const serverApiVersion = jsXHR.getResponseHeader(
            "Readlang-API-Version"
          );
          if (
            serverApiVersion &&
            serverApiVersion >
              sharedConfig.clientServerCompatibilityVersion.toString()
          ) {
            // Force a full page reload to ensure the client is up to date
            window.location.reload();
            return;
          }

          // TODO: dismiss notification ONLY if it's the server error one!
          //notificationBar.dismiss();
          callback(responseData, textStatus, jsXHR);
        },
        error: (jqXHR, textStatus) => {
          if (jqXHR.status === 0 && retryAttempt < retryDelaysMs.length) {
            const retryTimeoutID = setTimeout(() => {
              this.retryTimeoutIDs.delete(retryTimeoutID);
              that.remoteRequest(
                method,
                path,
                data,
                callback,
                errorCallback,
                jQueryOptions,
                retryAttempt + 1
              );
            }, retryDelaysMs[retryAttempt]);
            this.retryTimeoutIDs.add(retryTimeoutID);
            return;
          }

          that.handleError(
            jqXHR.status,
            jqXHR.responseJSON ?? jqXHR.responseText ?? textStatus,
            errorCallback
          );
        },
        cache: false,
      },
      jQueryOptions
    );

    if (/^(POST|PATCH|PUT|DELETE)$/.test(method)) {
      ajaxOptions.processData = false;
      if (data instanceof FormData) {
        // Send the form as-is and let the browser pick the multipart content
        // type, which has to carry the boundary it generated.
        ajaxOptions.contentType = false;
      } else {
        ajaxOptions.data = JSON.stringify(data);
        ajaxOptions.contentType = "application/json";
      }
    }

    if (window.READLANG.chromeExtension) {
      // Always use chromeExtensionRequestClient for requests from chrome extension, as it can
      // handle requests even when third party cookies are disabled

      // send message to background page to do request
      let response;

      try {
        response = await chromeExtensionRequestClient.request({
          method,
          url: this.baseURL + path,
          data,
          dataType: jQueryOptions?.dataType,
        });
      } catch (error) {
        console.log("handling error: ", error);
        this.handleError(error.status, error.data, errorCallback);
        throw error;
      }
      if (response.status >= 200 && response.status < 300) {
        callback(response.data, response.status);
      } else {
        this.handleError(response.status, response.data, errorCallback);
      }
    } else {
      $.ajax(ajaxOptions);
    }
  }

  /**
   * Make an API request.
   *
   * IMPORTANT: if errorCallback is provided, promises will not be rejected on
   * domain (4xx) failures — the callback alone handles those
   *
   * Error handling example:
   * ```
   * try {
   *   const response = await readlangClient.request("POST", "/api/...", data);
   * } catch (error: any) {
   *   const errorMessage = error.jqXHR?.data?.error || "Fallback message";
   * }
   * ```
   *
   * Terminal failures (network loss after retries, 5xx, 401 handled by the
   * global unauthorizedHandler) also invoke the error callback AND reject the
   * promise — even when an errorCallback is provided — with
   * `alreadyNotified: true` (on `error.jqXHR` when caught from the promise),
   * so awaited requests always settle rather than hang. handleError has
   * already shown a global notification bar by then, so callers should skip
   * their own error message in that case — `data` may be undefined or raw
   * HTML rather than the usual JSON body.
   */
  public request<T = any>(
    method: string,
    path: string,
    data: any,
    callback?: any,
    errorCallback?: any,
    jQueryOptions?: any
  ) {
    return new Promise<ReadlangResponse<T>>((resolve, reject) =>
      (async () => {
        // Settle before invoking caller callbacks — a throwing callback must
        // not leave the awaited promise pending forever. Awaiters still run
        // after the callback: microtasks only drain once the synchronous
        // callback returns (or throws).
        const successCallbackWrapper = (responseData, textStatus, jsXHR) => {
          resolve({ data: responseData, textStatus, jsXHR });
          callback?.(responseData, textStatus, jsXHR);
        };
        const errorCallbackWrapper = (jqXHR, textStatus, errorThrown) => {
          let callbackCompleted = false;
          try {
            errorCallback?.(jqXHR, textStatus, errorThrown);
            callbackCompleted = true;
          } finally {
            // Domain (4xx) errors remain callback-only, per the contract
            // above. Terminal failures — and any throwing errorCallback —
            // reject so awaited promises settle instead of hanging forever.
            if (
              !errorCallback ||
              jqXHR?.alreadyNotified ||
              !callbackCompleted
            ) {
              reject({ jqXHR, textStatus, errorThrown });
            }
          }
        };

        if (
          await localAPI.request(
            method,
            path,
            data,
            successCallbackWrapper,
            errorCallbackWrapper,
            // XXX This is so convoluted! Why do we need to pass remote request to localAPI??
            (_method, _path, _data, _successCallback, _errorCallback) => {
              this.remoteRequest(
                _method as "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
                _path,
                _data,
                _successCallback,
                _errorCallback,
                jQueryOptions
              );
            }
          )
        ) {
          return;
        }

        this.remoteRequest(
          method as "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
          path,
          data,
          successCallbackWrapper,
          errorCallbackWrapper,
          jQueryOptions
        );
      })()
    );
  }

  public login() {
    // try logging in
    this.promptForCredentials({
      stores: { user: this.user },
    });
  }

  public setUserModel(_user: any) {
    this.user = _user;
  }

  public disposeForTests() {
    clearTimeout(this.checkTimeout);
    this.checkTimeout = undefined;

    for (const timeoutID of this.retryTimeoutIDs) {
      clearTimeout(timeoutID);
    }
    this.retryTimeoutIDs.clear();
  }

  public async isLocalMode() {
    return await localAPI.isLocalMode();
  }

  public async setLocalMode(localMode: boolean) {
    await localAPI.setLocalMode(localMode);
  }

  public logout(callback?: () => void) {
    this.request("POST", "/api/logout", {}, async () => {
      await readlangLocalStorage.clearPromise();
      googleAuth.signOut();
      if (callback) {
        callback();
      } else {
        window.location.href = "/";
      }
    });
  }

  public fetchAuthenticatedState(callback: any) {
    $.ajax({
      type: "GET",
      url: this.baseURL + "/api/isAuthenticated",
      xhrFields: {
        withCredentials: true,
      },
      // Never pass an async callback directly to success or error since it it unreliable. Instead,
      // wrap it in a synchronous function
      success: () => {
        (async () => {
          await this.setLocalMode(false);
          callback();
        })();
      },
      error: () => {
        (async () => {
          await this.setLocalMode(true);
          callback();
        })();
      },
    });
  }
}

/**
 * Human-readable message for a rejected request() promise, for callers that
 * render the failure in the page. Recognizes `alreadyNotified` terminal
 * failures (the global notification bar already has the details) and avoids
 * "[object Object]" from stringifying the rejection.
 */
export const requestErrorMessage = (error: any): string => {
  if (!error?.jqXHR) {
    // Not a request() rejection (e.g. a thrown Error)
    return String(error);
  }
  if (error.jqXHR.alreadyNotified) {
    return "Request failed.";
  }
  const { data, status } = error.jqXHR;
  if (typeof data?.error === "string") {
    return data.error;
  }
  if (typeof data === "string" && data) {
    return data;
  }
  return `Request failed (${status ?? "unknown error"})`;
};

// Base every API request at the page's own origin, so requests stay same-origin
// with the correct scheme/host on localhost, LAN IPs, and the iOS app's `.local`
// host alike. Hard-coding https broke all API calls (→ 401 → forced local/trial
// mode) when the app loaded over plain http on a device's `.local` host.
export default new ReadlangClient(window.location.origin);
